import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, ContactBlock, EmptyState, QuickHelpRow, Skeleton, StayHeader } from '@/ds'
import { LinkRow } from '@/app/components/LinkRow'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { time, weekdayLongDay } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { useNow } from '@/lib/useNow'
import { PendingChangeBanner } from '../Reserva/PendingChangeBanner'
import { changeQuery } from '../Reserva/shared'
import { heroFor } from './hero'
import { ClimateCard } from './sections/ClimateCard'
import { chargesSubtitle, DepositAfterCard, DepositExplainer } from './sections/DepositCards'
import { KeySection } from './sections/KeySection'
import { RecoSection } from './sections/RecoSection'
import { StepsSection, useSteps } from './sections/StepsSection'
import { RequestSection } from './sections/RequestSection'
import { WifiCard } from './sections/WifiCard'
import { allDone, pendingCount, STEP_ROUTES } from './steps'
import { useStayData } from './useStayData'
import type { StayData } from './useStayData'
import type { Stay } from '@/services/reserva'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { checkoutService } from '@/services/checkout'
import type { Checkout as CheckoutData } from '@/services/checkout'
import { useApiLang } from '../Checkin/useSave'
import { LeaveAction } from '../Cierre/LeaveAction'
import { AccountLinks, CLOSE_ROUTES, RebookCard, ReviewCard } from '../Cierre/shared'
import { richText } from '@/lib/richText'
import './Estancia.css'

const HELP_ROUTE = '/ayuda'
const GUIDE_ROUTE = '/guia'
const DIRECTIONS_ROUTE = '/accesos/como-llegar'
const RECEIPTS_ROUTE = '/reserva/recibos'

interface PhaseProps {
  stay: Stay
  data: StayData
  lang: string
  now: Date
}

function hasKey(data: StayData): boolean {
  const key = asyncValue(data.key)
  return key !== null && key.state !== 'none'
}

/** Fase 1 · Antes de llegar: la lista de pasos manda. */
function Before({ stay, data, lang, now }: PhaseProps) {
  const { t } = useTranslation()
  const { steps, loading } = useSteps(stay, data, lang)
  const pending = pendingCount(steps)
  return (
    <>
      <h1>{t('v2.estancia.fases.before.titulo')}</h1>
      {!loading && pending > 0 && <p className="page__lead">{t(hasKey(data) ? 'v2.estancia.fases.before.lead' : 'v2.estancia.fases.before.leadSinLlave', { count: pending })}</p>}
      <StepsSection stay={stay} data={data} lang={lang} />
      {!loading && allDone(steps) && <KeySection stay={stay} data={data} lang={lang} now={now} variant="home" />}
      <RecoSection stay={stay} data={data} lang={lang} />
    </>
  )
}

/** Fase 2 · Día de llegada: la llave y cómo llegar; todo lo demás se retira. */
function Arrival({ stay, data, lang, now }: PhaseProps) {
  const { t } = useTranslation()
  const withKey = hasKey(data)
  return (
    <>
      <h1>{t('v2.estancia.fases.arrival.titulo')}</h1>
      <p className="page__lead">{t(withKey ? 'v2.estancia.fases.arrival.lead' : 'v2.estancia.fases.arrival.leadSinLlave', { time: time(stay.dates.checkIn, lang) })}</p>
      <StepsSection stay={stay} data={data} lang={lang} />
      <KeySection stay={stay} data={data} lang={lang} now={now} variant="home" />
      <LinkRow icon="home" title={t('v2.estancia.comoLlegar.titulo')} subtitle={stay.unit.address} to={DIRECTIONS_ROUTE} />
      <RecoSection stay={stay} data={data} lang={lang} />
    </>
  )
}

/** Fase 3 · Durante la estancia: lo que se usa cada día, con el pulgar. */
function During({ stay, data, lang, now }: PhaseProps) {
  const { t } = useTranslation()
  const key = asyncValue(data.key)
  const compactKey = key?.state === 'active' && key.accesses.some((a) => a.openable)
  const guideItems = [...stay.guide.manual, ...stay.guide.conditions]
  return (
    <>
      <h1>{t('v2.estancia.fases.during.titulo')}</h1>
      <p className="page__lead">{t('v2.estancia.fases.during.lead', { when: t('v2.estancia.cabecera.aLas', { day: weekdayLongDay(stay.dates.checkOut, lang), time: time(stay.dates.checkOut, lang) }) })}</p>
      {data.key.kind === 'loading' ? (
        <Skeleton height={96} radius={24} />
      ) : (
        <>
          {(compactKey || stay.wifi.ssid) && (
            <div className="estancia__grid">
              {compactKey && <KeySection stay={stay} data={data} lang={lang} now={now} variant="compact" />}
              <WifiCard stay={stay} />
            </div>
          )}
          {key?.state === 'locked' && <KeySection stay={stay} data={data} lang={lang} now={now} variant="home" />}
        </>
      )}
      <ClimateCard data={data} lang={lang} />
      <RequestSection stay={stay} />
      {guideItems.length > 0 && <LinkRow icon="book" title={t('v2.estancia.guia.titulo')} subtitle={guideItems.slice(0, 5).map((g) => g.name).join(', ')} to={GUIDE_ROUTE} />}
      <RecoSection stay={stay} data={data} lang={lang} />
    </>
  )
}

/** Fase 4 · Día de salida (Inicio.dc.html phase=checkout): tres cosas al salir, "Ya hemos salido", salida tardía, fianza y cargos. */
function Checkout({ stay, data, lang, checkout, onLeft }: PhaseProps & { checkout: CheckoutData | null; onLeft: (leftAt: string) => void }) {
  const { t } = useTranslation()
  const charges = chargesSubtitle(data, lang, t)
  return (
    <>
      <h1>{t('v2.estancia.fases.checkout.titulo')}</h1>
      <p className="page__lead">{t('v2.estancia.fases.checkout.lead', { time: time(stay.dates.checkOut, lang) })}</p>
      {checkout === null ? (
        <Skeleton height={200} radius={16} />
      ) : (
        <Card>
          <div className="cierre__card">
            {checkout.checklist.length > 0 && (
              <ul className="cierre__bullets">
                {checkout.checklist.map((it) => (
                  <li key={it.id}>{it.text}</li>
                ))}
              </ul>
            )}
            {checkout.checklist.length === 0 && checkout.instructions && <div className="cierre__rich">{richText(checkout.instructions.description)}</div>}
            <LeaveAction checkout={checkout} help={t('v2.estanciaSalida.ayuda')} onLeft={onLeft} />
          </div>
        </Card>
      )}
      <LinkRow icon="clock" title={t('v2.estancia.salida.titulo')} subtitle={time(stay.dates.checkOut, lang)} to={CLOSE_ROUTES.checkout} tabular />
      <RecoSection stay={stay} data={data} lang={lang} />
      <DepositExplainer deposit={data.deposit} lang={lang} origin="estancia" />
      {charges && <LinkRow icon="wallet" title={t('v2.estancia.cargos.titulo')} subtitle={charges} to={STEP_ROUTES.payments} query={changeQuery('estancia')} tabular />}
    </>
  )
}

/** Fase 5 · Después (Inicio.dc.html phase=after): fianza, factura, reseña y volver a reservar. */
function After({ stay, data, lang }: PhaseProps) {
  const { t } = useTranslation()
  const [after] = useAsync(`estancia-despues:${stay.guestCode}`, (signal) => checkoutService.getAfterStay(stay, signal))
  const a = asyncValue(after)
  return (
    <>
      <h1>{t('v2.estancia.fases.after.titulo')}</h1>
      <p className="page__lead">{t('v2.estancia.fases.after.lead')}</p>
      <DepositAfterCard deposit={data.deposit} lang={lang} origin="estancia" />
      {stay.flags.showGetBills && (
        <LinkRow icon="file-text" title={t('v2.estancia.despues.factura')} subtitle={stay.bills.length > 0 ? t('v2.estancia.despues.facturas', { count: stay.bills.length }) : t('v2.legacy.preview.labels.requestInvoice.seeInvoice')} to={RECEIPTS_ROUTE} tabular />
      )}
      {after.kind === 'loading' && <Skeleton height={160} radius={16} />}
      {a && <ReviewCard review={a.review} />}
      {a && <RebookCard rebook={a.rebook} />}
      <LinkRow icon="calendar" title={t('v2.salida.despues')} subtitle={t('v2.salida.despuesSub')} to={CLOSE_ROUTES.after} />
    </>
  )
}

function Cancelled({ stay }: { stay: Stay }) {
  const { t } = useTranslation()
  return (
    <>
      <EmptyState icon="x-circle" headingLevel={1} title={t('v2.legacy.texts.Reservation_Cancelled')} />
      {stay.contact.emergencyPhone && <ContactBlock {...(stay.contact.whatsapp ? { whatsappHref: whatsappHref(stay.contact.whatsapp, stay.reference) } : {})} emergencyPhone={stay.contact.emergencyPhone} />}
    </>
  )
}

/** Tu estancia (8.1): cabecera, pregunta del momento, una acción principal, una recomendación y Ayuda siempre visible. */
export function Estancia() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const stay = useLoadedStay()
  const data = useStayData(stay)
  const now = useNow(stay.phase === 'checkout' ? 30000 : 60000)
  const { search, onLinkClick } = useAppNavigate()
  const hero = heroFor(stay, now, lang, t)
  const image = stay.property.coverImageUrl ?? stay.unit.photos[0]
  const props: PhaseProps = { stay, data, lang, now }
  const apiLang = useApiLang()
  const [checkoutData] = useAsync(`estancia-salida:${stay.guestCode}:${stay.phase}:${apiLang}`, (signal) => (stay.phase === 'checkout' ? checkoutService.getCheckout(stay, apiLang, signal) : Promise.resolve(null)))
  const [leftAt, setLeftAt] = useState<string | null>(null)
  const checkout = asyncValue(checkoutData)
  // "Ya hemos salido" adelanta la fase a "después" (D5); también si la salida ya estaba avisada.
  const phase = stay.phase === 'checkout' && (leftAt || checkout?.left) ? 'after' : stay.phase

  let body: React.ReactNode
  switch (phase) {
    case 'before':
      body = <Before {...props} />
      break
    case 'arrival':
      body = <Arrival {...props} />
      break
    case 'during':
      body = <During {...props} />
      break
    case 'checkout':
      body = <Checkout {...props} checkout={checkoutData.kind === 'loading' ? null : (checkout ?? { time: stay.dates.checkOut, checklist: [], instructions: null, left: false, leftAt: null, canMarkLeft: false, charges: [], total: null, allPaid: null })} onLeft={setLeftAt} />
      break
    case 'after':
      body = <After {...props} />
      break
    default:
      body = <Cancelled stay={stay} />
  }

  return (
    <main className="page page--wide estancia">
      <StayHeader
        headingLevel={2}
        propertyName={stay.property.name}
        subtitle={hero.subtitle}
        {...(hero.countdown ? { countdown: hero.countdown } : {})}
        {...(hero.label ? { countdownLabel: hero.label } : {})}
        {...(image ? { image } : {})}
        imageLabel={t('v2.estancia.cabecera.fotoPortada', { name: stay.unit.name })}
      />
      {stay.phase !== 'cancelled' && <AccountLinks />}
      {/* D45: cambio de reserva aplicado y sin pagar, con la acción de pagar. */}
      {stay.phase !== 'cancelled' && asyncValue(data.pendingChange) && <PendingChangeBanner pending={asyncValue(data.pendingChange)!} origin="estancia" />}
      {body}
      <QuickHelpRow href={withQuery(HELP_ROUTE, search)} onClick={onLinkClick(HELP_ROUTE)} className="estancia__help" />
    </main>
  )
}
